import { HttpErrorResponse } from '@angular/common/http';
import { ApiError, ApiErrorFieldModel } from '../../models/errors/api-error.model';

/**
 * Estrazione di messaggio, codice e campi dagli errori delle API.
 *
 * Due formati convivono:
 * - **ecommerce** (contratto 09/09/2026): envelope `{ ok: false, error: { code,
 *   message, original_status, fields } }`, già tradotto in `ApiError` da
 *   `ecommerceApiErrorInterceptor`. I messaggi sono in italiano e mostrabili.
 * - **gestionale `/v1.0`** e validazione FastAPI: `{ "detail": "..." }`,
 *   `{ "detail": { "message": "..." } }` oppure l'array di validazione, i cui
 *   `msg` sono tecnici e in inglese e non vanno mostrati.
 */
interface LegacyErrorDetail {
  code?: unknown;
  message?: unknown;
}

function readLegacyDetail(error: unknown): string | LegacyErrorDetail | unknown[] | null {
  if (!(error instanceof HttpErrorResponse)) {
    return null;
  }
  return (error.error as { detail?: unknown } | null)?.detail ?? null;
}

/** Messaggio da mostrare all'utente, o il fallback se non ce n'è uno presentabile. */
export function extractApiErrorDetail(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    return error.message.trim() || fallback;
  }

  const detail = readLegacyDetail(error);

  if (typeof detail === 'string' && detail.trim().length > 0) {
    return detail;
  }

  if (detail && !Array.isArray(detail) && typeof detail === 'object') {
    const message = (detail as LegacyErrorDetail).message;
    if (typeof message === 'string' && message.trim().length > 0) {
      return message;
    }
  }

  return fallback;
}

/**
 * Codice applicativo dell'errore (es. `ACCOUNT_EXISTS`). Va preferito allo
 * status HTTP: gli errori applicativi ecommerce viaggiano tutti su HTTP 200.
 */
export function extractApiErrorCode(error: unknown): string | null {
  if (error instanceof ApiError) {
    return error.code;
  }

  const detail = readLegacyDetail(error);

  if (detail && !Array.isArray(detail) && typeof detail === 'object') {
    const code = (detail as LegacyErrorDetail).code;
    if (typeof code === 'string' && code.trim().length > 0) {
      return code;
    }
  }

  return null;
}

/**
 * Errori per campo di `REQUEST_VALIDATION_ERROR`: `path` indica il campo del
 * payload (es. `account.password`), utile per segnalarlo nel form.
 */
export function extractApiErrorFields(error: unknown): ApiErrorFieldModel[] {
  return error instanceof ApiError ? error.fields : [];
}

/** Status "vero" dell'errore secondo il contratto HTTP standard. */
export function extractApiErrorStatus(error: unknown): number | null {
  if (error instanceof ApiError) {
    return error.originalStatus;
  }
  return error instanceof HttpErrorResponse ? error.status : null;
}
